<template>
  <!-- Einzeilige Filter ohne Button, FlatPickr im dd.mm.yyyy-Format -->
  <section class="card p-3">
    <div
      class="form"
      style="display:grid; gap:12px;grid-template-columns: 120px 120px "
    >
      <div class="field m-0">
        <label for="from">Datum von</label>
        <flat-pickr
          id="from"
          v-model="from"
          class="input"
          :config="dateConfig"
          :disabled="loading"
          @on-change="onDateChange"
        />
      </div>

      <div class="field m-0">
        <label for="to">Datum bis</label>
        <flat-pickr
          id="to"
          v-model="to"
          class="input"
          :config="dateConfig"
          :disabled="loading"
          @on-change="onDateChange"
        />
      </div>
    </div>
  </section>

  <section class="card">
    <div class="">
      <h3
        class="h2"
        style="font-size:16px; line-height:22px; font-weight:500; margin:0 0 18px;"
      >
        Fehlzeiten-Check (Event liegt im Aufenthalt)
      </h3>

      <div
        v-if="errConsistency"
        class="notice notice-danger"
        role="alert"
      >
        {{ errConsistency }}
      </div>

      <div v-else>
        <div v-if="loadingConsistency">
          Lade…
        </div>

        <div
          v-else-if="consistency.items.length === 0"
          class="muted"
        >
          Keine Auffälligkeiten im Zeitraum.
        </div>

        <div
          v-else
          class="table"
        >
          <div class="table-lateness">
            <div class="muted header-cell">
              Datum
            </div>
            <div class="muted header-cell">
              Schüler
            </div>
            <div class="muted header-cell">
              Angebot
            </div>
            <div class="muted header-cell">
              Zeit
            </div>
            <div class="muted header-cell">
              Fehlzeit
            </div>

            <template
              v-for="row in consistency.items"
              :key="`${row.stayId}-${row.eventParticipantId}`"
            >
              <div class="body-cell">
                {{ formatDateDisplay(row.date) }}
              </div>

              <div class="body-cell">
                {{ row.lastName }}, {{ row.firstName }}
              </div>

              <div class="body-cell">
                {{ row.offerName }}
              </div>

              <div class="body-cell">
                {{ row.eventStartHm }} – {{ row.eventEndHm }}
              </div>

              <div class="body-cell">
                {{ formatMinutes(row.latenessMinutes) }} min
                <span class="muted">
                  ({{ row.arrivedHm }} – {{ row.releasedHm }})
                </span>
              </div>
            </template>
          </div>

          <div style="display:flex; gap:8px; align-items:center; margin-top:8px;">
            <button
              class="btn"
              :disabled="consistencyPage<=1 || loading"
              @click="changeConsistencyPage(consistencyPage-1)"
            >
              Zurück
            </button>
            <span class="muted">Seite {{ consistencyPage }} / {{ consistencyTotalPages }}</span>
            <button
              class="btn"
              :disabled="consistencyPage>=consistencyTotalPages || loading"
              @click="changeConsistencyPage(consistencyPage+1)"
            >
              Weiter
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="card">
    <div class="">
      <h3
        class="h2"
        style="font-size:16px; line-height:22px; font-weight:500; margin:0 0 18px;"
      >
        leerer Aufenthalt
      </h3>

      <div
        v-if="errEmpty"
        class="notice notice-danger"
        role="alert"
      >
        {{ errEmpty }}
      </div>

      <div v-else>
        <div v-if="loadingEmpty">
          Lade…
        </div>

        <div
          v-else-if="emptyStays.items.length === 0"
          class="muted"
        >
          Keine Treffer im Zeitraum.
        </div>

        <div
          v-else
          class="table"
        >
          <!-- Eine Zeile je Datum + Schülerliste, je Name klickbar -->
          <table class="empty-stays-table">
            <thead>
              <tr>
                <th scope="col">
                  Datum
                </th>
                <th scope="col">
                  Schüler
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="g in pagedGroupedEmpty"
                :key="g.date"
              >
                <td class="empty-stays-date">
                  {{ formatDateDisplay(g.date) }}
                </td>

                <td class="empty-stays-students">
                  <div class="student-chip-list">
                    <button
                      v-for="s in g.students"
                      :key="s.fullName"
                      type="button"
                      class="btn btn-xs student-chip"
                      :title="`Tagesansicht öffnen: ${s.fullName}`"
                      @click="openOffersDayViewForStudent(s.fullName, g.date)"
                    >
                      {{ s.fullName }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination: bezieht sich auf DATEN (nicht auf Schüler-Zeilen) -->
          <div style="display:flex; gap:8px; align-items:center; margin-top:8px;">
            <button
              class="btn"
              :disabled="emptyDatePage <= 1 || loading"
              @click="changeEmptyDatePage(emptyDatePage - 1)"
            >
              Zurück
            </button>
            <span class="muted">Seite {{ emptyDatePage }} / {{ emptyDateTotalPages }}</span>
            <button
              class="btn"
              :disabled="emptyDatePage >= emptyDateTotalPages || loading"
              @click="changeEmptyDatePage(emptyDatePage + 1)"
            >
              Weiter
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="card">
    <div class="">
      <h3
        class="h2"
        style="font-size:16px; line-height:22px; font-weight:500; margin:0 0 18px;"
      >
        Ferienbetrieb
      </h3>

      <div
        v-if="errHoliday"
        class="notice notice-danger"
        role="alert"
      >
        {{ errHoliday }}
      </div>

      <div v-else>
        <div v-if="loadingHoliday">
          Lade…
        </div>

        <div
          v-else-if="holidayDays.items.length === 0"
          class="muted"
        >
          Keine Treffer im Zeitraum.
        </div>

        <div
          v-else
          class="table"
        >
          <div
            style="display:grid; grid-template-columns: 140px repeat(3, 1fr); gap:8px; border-top:1px solid var(--border);"
          >
            <div
              class="muted"
              style="padding:6px 0;"
            >
              Datum
            </div>
            <div
              class="muted"
              style="padding:6px 0;"
            >
              freiwillig
            </div>
            <div
              class="muted"
              style="padding:6px 0;"
            >
              pflicht
            </div>
            <div
              class="muted"
              style="padding:6px 0;"
            >
              Pflicht-Checkins
            </div>

            <template
              v-for="row in holidayDays.items"
              :key="row.date"
            >
              <div style="padding:8px 0; border-bottom:1px solid var(--border);">
                {{ formatDateDisplay(row.date) }}
              </div>
              <div style="padding:8px 0; border-bottom:1px solid var(--border);">
                {{ row.voluntaryCount }}
              </div>
              <div style="padding:8px 0; border-bottom:1px solid var(--border);">
                {{ row.mandatoryCount }}
              </div>
              <div style="padding:8px 0; border-bottom:1px solid var(--border);">
                {{ row.mandatoryWithCheckins }}
              </div>
            </template>
          </div>

          <div style="display:flex; gap:8px; align-items:center; margin-top:8px;">
            <button
              class="btn"
              :disabled="holidayPage<=1 || loading"
              @click="changeHolidayPage(holidayPage-1)"
            >
              Zurück
            </button>
            <span class="muted">Seite {{ holidayPage }} / {{ holidayTotalPages }}</span>
            <button
              class="btn"
              :disabled="holidayPage>=holidayTotalPages || loading"
              @click="changeHolidayPage(holidayPage+1)"
            >
              Weiter
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue';
import api from '@/services/api';
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {German} from 'flatpickr/dist/l10n/de.js';

function todayYmd() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const y = d.getFullYear();
  const m = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  return `${y}-${m}-${day}`;
}

function minusMonthsYmd(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  const pad = (v) => String(v).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const from = ref(minusMonthsYmd(6));
const to = ref(todayYmd());

const loading = ref(false);

// Consistency Report
const consistency = ref({items: [], meta: {total: 0}});
const consistencyPage = ref(1);
const consistencySize = ref(50);
const loadingConsistency = ref(false);
const errConsistency = ref('');

const consistencyTotalPages = computed(() => {
  const total = consistency.value?.meta?.total ?? 0;
  return Math.max(1, Math.ceil(total / consistencySize.value));
});

function formatMinutes(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  const rounded = Math.round(n * 10) / 10;
  return String(rounded).replace('.', ',');
}

async function loadConsistency() {
  loadingConsistency.value = true;
  errConsistency.value = '';
  try {
    const {data} = await api.get('/reports/lateness-consistency', {
      params: {
        from: from.value,
        to: to.value,
        page: consistencyPage.value,
        size: consistencySize.value
      }
    });

    if (data?.success) {
      const items = (data.data?.items ?? []).map(r => ({
        date: r.date,
        studentId: r.studentId,
        firstName: r.firstName,
        lastName: r.lastName,
        offerName: r.offerName,
        eventStartHm: r.eventStartHm,
        eventEndHm: r.eventEndHm,
        arrivedHm: r.arrivedHm,
        releasedHm: r.releasedHm,
        latenessSeconds: r.latenessSeconds ?? 0,
        latenessMinutes: r.latenessMinutes ?? 0,
        stayId: r.stayId,
        eventParticipantId: r.eventParticipantId
      }));
      const total = data.data?.meta?.total ?? 0;
      consistency.value = {items, meta: {total}};
    } else {
      throw new Error(data?.error || 'Unbekannter Fehler');
    }
  } catch (e) {
    errConsistency.value = e?.message || String(e);
    consistency.value = {items: [], meta: {total: 0}};
  } finally {
    loadingConsistency.value = false;
  }
}

function changeConsistencyPage(p) {
  consistencyPage.value = Math.min(Math.max(1, p), consistencyTotalPages.value);
  loadConsistency();
}

// FlatPickr-Konfiguration (Anzeige dd.mm.yyyy, Wert ISO)
const dateConfig = {
  locale: German,
  dateFormat: 'Y-m-d', // Wert im v-model
  altInput: true,
  altFormat: 'd.m.Y', // Anzeige im Feld (dd.mm.yyyy)
  allowInput: true,
  time_24hr: true,
};

// Empty Stays
const emptyStays = ref({items: [], meta: {total: 0}});

// Pagination soll auf DATUM basieren (clientseitig), nicht auf Anzahl Schüler
const emptyDatePage = ref(1);
const emptyDatePageSize = ref(10);

const loadingEmpty = ref(false);
const errEmpty = ref('');

/**
 * Gruppierung nach Datum:
 * - eine Zeile je Datum
 * - zweite Spalte: Schüler als kommagetrennte Liste (Vorname Nachname), je Name klickbar
 * - dedupliziert je Datum nach Fullname
 */
const groupedEmptyAll = computed(() => {
  const items = Array.isArray(emptyStays.value?.items) ? emptyStays.value.items : [];
  const byDate = new Map(); // date -> Map(fullName -> {fullName})

  for (const r of items) {
    const date = r?.date ?? null;
    if (!date) continue;

    const firstName = String(r?.firstName ?? '').trim();
    const lastName = String(r?.lastName ?? '').trim();
    const fullName = `${firstName} ${lastName}`.trim();

    if (!byDate.has(date)) {
      byDate.set(date, new Map());
    }
    if (fullName) {
      byDate.get(date).set(fullName, {fullName});
    }
  }

  const dates = Array.from(byDate.keys()).sort(); // ISO-Strings sortieren
  return dates.map((d) => {
    const students = Array.from(byDate.get(d).values())
        .sort((a, b) => a.fullName.localeCompare(b.fullName, 'de-DE', {sensitivity: 'base'}));
    return {date: d, students};
  });
});

const emptyDateTotalPages = computed(() => {
  const totalDates = groupedEmptyAll.value.length;
  return Math.max(1, Math.ceil(totalDates / emptyDatePageSize.value));
});

const pagedGroupedEmpty = computed(() => {
  const page = Math.max(1, emptyDatePage.value);
  const size = Math.max(1, emptyDatePageSize.value);
  const start = (page - 1) * size;
  const end = start + size;
  return groupedEmptyAll.value.slice(start, end);
});

// Holiday Days
const holidayDays = ref({items: [], meta: {total: 0}});
const holidayPage = ref(1);
const holidaySize = ref(20);
const loadingHoliday = ref(false);
const errHoliday = ref('');

const holidayTotalPages = computed(() => {
  const total = holidayDays.value?.meta?.total ?? 0;
  return Math.max(1, Math.ceil(total / holidaySize.value));
});

// Formatierung YYYY-MM-DD -> dd.mm.yyyy ohne TZ-Fehler
function formatDateDisplay(ymd) {
  if (!ymd || typeof ymd !== 'string' || ymd.length < 10) return ymd || '';
  const [y, m, d] = ymd.slice(0, 10).split('-');
  if (!y || !m || !d) return ymd;
  return `${d}.${m}.${y}`;
}

function openOffersDayViewForStudent(fullName, ymd) {
  const name = String(fullName || '').trim();
  if (!name) return;

  const date = String(ymd || '').trim();
  const hasValidDate = /^\d{4}-\d{2}-\d{2}$/.test(date);

  const params = new URLSearchParams({
    group: 'studentPresence',
    timelineFilter: 'all',
    search: name
  });

  if (hasValidDate) {
    params.set('date', date);
  }

  const url = `/offers-day-view?${params.toString().replace(/%20/g, '+')}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

function changeEmptyDatePage(p) {
  emptyDatePage.value = Math.min(Math.max(1, p), emptyDateTotalPages.value);
}

// Für den Report holen wir bewusst "viele" Zeilen und paginieren danach nach Datum im Frontend.
// Hinweis: Backend capped "size" aktuell, daher maximaler Wert nutzen.
async function loadEmptyStays() {
  loadingEmpty.value = true;
  errEmpty.value = '';
  try {
    const {data} = await api.get('/reports/empty-stays', {
      params: {
        from: from.value,
        to: to.value,
        page: 1,
        size: 200
      }
    });

    if (data?.success) {
      const items = (data.data?.items ?? []).map(r => ({
        date: r.date,
        studentId: r.studentId,
        firstName: r.firstName,
        lastName: r.lastName
      }));
      const total = data.data?.meta?.total ?? 0;
      emptyStays.value = {items, meta: {total}};
    } else {
      throw new Error(data?.error || 'Unbekannter Fehler');
    }
  } catch (e) {
    errEmpty.value = e?.message || String(e);
    emptyStays.value = {items: [], meta: {total: 0}};
  } finally {
    loadingEmpty.value = false;
  }
}

async function loadHolidayDays() {
  loadingHoliday.value = true;
  errHoliday.value = '';
  try {
    const {data} = await api.get('/reports/holiday-operation-days', {
      params: {from: from.value, to: to.value, page: holidayPage.value, size: holidaySize.value}
    });

    if (data?.success) {
      const items = (data.data?.items ?? []).map(r => ({
        date: r.date,
        voluntaryCount: r.voluntaryCount ?? 0,
        mandatoryCount: r.mandatoryCount ?? 0,
        mandatoryWithCheckins: r.mandatoryWithCheckins ?? 0
      }));
      const total = data.data?.meta?.total ?? 0;
      holidayDays.value = {items, meta: {total}};
    } else {
      throw new Error(data?.error || 'Unbekannter Fehler');
    }
  } catch (e) {
    errHoliday.value = e?.message || String(e);
    holidayDays.value = {items: [], meta: {total: 0}};
  } finally {
    loadingHoliday.value = false;
  }
}

async function reload() {
  loading.value = true;
  try {
    emptyDatePage.value = 1;
    holidayPage.value = 1;
    consistencyPage.value = 1;
    await Promise.all([loadEmptyStays(), loadHolidayDays(), loadConsistency()]);
  } finally {
    loading.value = false;
  }
}

function onDateChange() {
  reload();
}

function changeHolidayPage(p) {
  holidayPage.value = p;
  loadHolidayDays();
}

onMounted(() => {
  reload();
});
</script>

<style scoped>
.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 14px;
}

.muted {
  color: var(--muted);
  font-size: 12px;
}

.table .muted {
  color: var(--muted);
}

.table-lateness {
  display: grid;
  grid-template-columns: 120px 300px 1fr 150px  200px;
  border-top: 1px solid var(--border);
}

.table-lateness .header-cell {
  padding: 6px 0;
}

.table-lateness .body-cell {
  padding: 2px 8px;
  border-bottom: 1px solid var(--border);
  font-size: 13px;
}

/* Einzeilige, konsistente Inputs */
.input {
  height: 38px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
}

.input:focus {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}

/* Optische Trennung zwischen Tabellen */
.section-sep {
  border: 0;
  border-top: 1px solid var(--border-strong);
  margin: 16px 0;
}

/* "Leere Stays" als echte Tabelle */
.empty-stays-table {
  width: 100%;
  border-collapse: collapse;
}

.empty-stays-table thead th {
  text-align: left;
  padding: 6px 0;
  font-weight: 700; /* Head bold */
  font-size: 12px;
  color: var(--muted);
  border-bottom: 1px solid var(--border);
}

.empty-stays-table tbody td {
  padding: 8px 0;
  border-bottom: 1px solid var(--border);
  vertical-align: top;
}

.empty-stays-date {
  width: 140px;
  white-space: nowrap;
}

.empty-stays-students {
  width: auto;
}

.student-link {
  color: var(--primary);
  text-decoration: none;
}

.student-link:hover {
  text-decoration: underline;
}

.student-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-radius: 6px;
}

/* XS-Button/Chips für Schüler */
.student-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
}

.btn-xs {
  height: 22px;
  line-height: 20px;
  padding: 0 8px;
  font-size: 12px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
}

.btn-xs:hover {
  background: rgba(79, 70, 229, 0.06);
  border-color: var(--primary);
}

.btn-xs:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
}

.student-chip {
  white-space: nowrap;
}

/* Kleiner Link-Button neben dem Schüler */
.icon-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-left: 2px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--primary);
  text-decoration: none;
}

.icon-link:hover {
  background: rgba(79, 70, 229, 0.06);
  text-decoration: none;
}

.icon-link .pi {
  font-size: 12px;
}
</style>